import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useDeskHabit } from '@/hooks/useDeskHabit';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { trackActivity } from '@/lib/activityTracking';
import { lotCelebrationCopy, LOT_PUBLISHED_EVENT } from '@/lib/deskHabit';
import DeskMark from '@/components/desk/DeskMark';

export const FEATURE_META: FeatureMeta = {
  id: 'lot-celebration',
  label: 'New listing celebration',
  description: 'When one of your first listings goes live, a short moment offers to add another listing or put it on a menu and share it.',
  triggerType: 'dialog',
  personas: ['seller', 'buyer_seller'],
};

/** Listens for a first-time publish; fires only while the first listings are still coming in. */
export default function LotCelebration() {
  useFeatureRegistration(FEATURE_META);
  const navigate = useNavigate();
  const { refetch } = useDeskHabit();
  const [copy, setCopy] = useState<{ title: string; body: string; n: number; variant: 'first' | 'middle' | 'target' } | null>(null);

  useEffect(() => {
    const onPublished = async () => {
      const { data } = await refetch();
      const lots = data?.reps.find((r) => r.key === 'lots');
      if (!data?.celebrationsEnabled || !lots) return;
      const c = lotCelebrationCopy(lots.count, lots.target);
      if (!c) return;
      setCopy({ ...c, n: lots.count });
      trackActivity('desk_celebration_shown', { kind: 'lot', rep_count: lots.count });
    };
    window.addEventListener(LOT_PUBLISHED_EVENT, onPublished);
    return () => window.removeEventListener(LOT_PUBLISHED_EVENT, onPublished);
  }, [refetch]);

  const act = (action: 'add_another' | 'menu_share' | 'dismiss', to?: string) => {
    trackActivity('desk_celebration_action', { kind: 'lot', action, rep_count: copy?.n ?? 0 });
    setCopy(null);
    if (to) navigate(to);
  };

  return (
    <Dialog open={!!copy} onOpenChange={(v) => !v && act('dismiss')}>
      <DialogContent className="sm:max-w-md desk-paper rounded-2xl border-0 p-0 overflow-hidden">
        <div className="px-8 pt-10 pb-8 text-center">
          <div className="flex justify-center"><DeskMark className="h-10 w-14" /></div>
          <DialogHeader className="mt-5 space-y-3 text-center sm:text-center">
            <DialogTitle className="font-display text-3xl font-normal tracking-tight text-balance">{copy?.title}</DialogTitle>
            <DialogDescription className="text-balance">{copy?.body}</DialogDescription>
          </DialogHeader>
          <div className="mt-8 flex flex-col gap-2">
            {copy?.variant === 'target' ? (
              <>
                <Button variant="guide" onClick={() => act('menu_share', '/my-menu?intent=add-listings')}>Add it to your VIP menu</Button>
                <Button variant="quiet" onClick={() => act('add_another', '/listings/new')}>Add another listing</Button>
              </>
            ) : (
              <>
                <Button variant="guide" onClick={() => act('add_another', '/listings/new')}>Add another listing</Button>
                <Button variant="quiet" onClick={() => act('menu_share', '/my-menu?intent=add-listings')}>Put it on a menu</Button>
              </>
            )}
            <Button variant="quiet" className="text-muted-foreground" onClick={() => act('dismiss')}>Done for now</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
